/**
 * Green Flag — MatchNotification
 *
 * The celebratory toast that pops in the lower-right when two people spark
 * each other back — a mutual match. A confetti burst fires behind a small
 * white card: the two matched faces overlapping with a spark badge between
 * them, a flirty headline, and a "View Your Matches" CTA to /matches.
 *
 * Layout & positioning are owned here (position: fixed, bottom-right, above
 * the bottom nav) so a caller can render `<MatchNotification .../>` anywhere
 * and it lands in the right place. Dismissal and navigation are lifted to the
 * caller via `onViewMatches` / `onDismiss` — this component stays
 * router-agnostic so it drops cleanly into Storybook and the app alike.
 */

import { Box, Button, HStack, Text, VStack, chakra } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useMediaBlob } from "../../lib/mediaHelpers";
import { useMeasuredVariantPath } from "../../lib/profileMedia";
import { Icon } from "../Icon/Icon";

export interface MatchNotificationProps {
  /** The match's first name — drives the flirty subline ("You and Maya…"). */
  name: string;
  /**
   * Dating or friendship. A mutual Spark on the post-event Friends tab is a
   * friendship, and telling those two people they "liked each other" is the
   * one romantic framing nobody in Friends Only mode ever opted into.
   */
  category?: "dating" | "friend";
  /** Authenticated media path (or raw http URL in Storybook) for the match's photo. */
  matchPhoto?: string;
  /** The viewer's own photo — the near circle in the overlapping pair. */
  yourPhoto?: string;
  /** Fired by the CTA; wire to `navigate("/matches")`. */
  onViewMatches?: () => void;
  /** Fired by the close button. */
  onDismiss?: () => void;
  /**
   * Set false to freeze the confetti (Storybook docs, reduced-motion, or
   * re-renders where we don't want the burst to replay). Defaults on.
   */
  confetti?: boolean;
}

const MotionDiv = motion.div;

export function MatchNotification({
  name,
  category = "dating",
  matchPhoto,
  yourPhoto,
  onViewMatches,
  onDismiss,
  confetti = true,
}: MatchNotificationProps) {
  const isFriend = category === "friend";
  return (
    <Box
      position="fixed"
      right={{ base: "4", md: "6" }}
      // Sits above the bottom nav on mobile; hugs the corner on desktop.
      bottom={{ base: "88px", md: "6" }}
      zIndex="toast"
      role="status"
      aria-live="polite"
      pointerEvents="none"
    >
      <Box position="relative" pointerEvents="auto">
        {confetti && <Confetti />}
        <MotionDiv
          initial={{ opacity: 0, y: 24, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.92 }}
          transition={{ type: "spring", stiffness: 380, damping: 26 }}
          style={{ position: "relative", zIndex: 1 }}
        >
          <Box
            layerStyle="floating"
            borderRadius="lg"
            bg="bg.canvas"
            p="4"
            pr="5"
            w="20rem"
            maxW="calc(100vw - 2rem)"
          >
            <HStack gap="3" align="center">
              <MatchAvatars
                yourPhoto={yourPhoto}
                matchPhoto={matchPhoto}
                isFriend={isFriend}
              />

              <VStack gap="0.5" align="flex-start" flex="1" minW="0">
                <Text textStyle="cardTitle" color="fg.default" lineClamp={1}>
                  {isFriend
                    ? "You’ve made a new friend! 🎉"
                    : "It’s a mutual spark! ⚡"}
                </Text>
                <Text textStyle="bodyLight" color="fg.muted" lineClamp={2}>
                  {isFriend
                    ? `You and ${name} added each other as friends 💚`
                    : `You and ${name} liked each other 💚`}
                </Text>
              </VStack>

              <chakra.button
                type="button"
                aria-label="Dismiss"
                onClick={onDismiss}
                appearance="none"
                bg="transparent"
                border="none"
                p="1"
                m="-1"
                color="fg.muted"
                cursor="pointer"
                alignSelf="flex-start"
                flexShrink={0}
                _hover={{ color: "fg.default" }}
              >
                <Icon name="x" boxSize="14px" />
              </chakra.button>
            </HStack>

            <Button
              mt="3"
              w="100%"
              size="lg"
              onClick={onViewMatches}
              gap="2"
            >
              View Your Matches
              <Icon name="chevron-right" boxSize="12px" />
            </Button>
          </Box>
        </MotionDiv>
      </Box>
    </Box>
  );
}

/**
 * The overlapping face pair with a spark badge nested where they meet. Uses
 * the same auth-blob avatar pipeline as SparksChip so real profile paths
 * render un-blurred here, while raw http URLs (Storybook) bypass the blob
 * fetch. Faces are sharp — this is the payoff moment, not the anonymous crowd.
 */
function MatchAvatars({
  yourPhoto,
  matchPhoto,
  isFriend,
}: {
  yourPhoto?: string;
  matchPhoto?: string;
  isFriend?: boolean;
}) {
  return (
    <Box position="relative" flexShrink={0} w="60px" h="44px">
      <Avatar path={yourPhoto} left="0" zIndex={1} />
      <Avatar path={matchPhoto} left="24px" zIndex={2} />
      <Box
        position="absolute"
        left="22px"
        bottom="-2px"
        boxSize="20px"
        borderRadius="full"
        bg={isFriend ? "bg.friends" : "bg.brand"}
        color="fg.onBrand"
        border="2px solid"
        borderColor="bg.canvas"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={3}
      >
        <Icon name={isFriend ? "smile" : "spark-filled"} boxSize="10px" />
      </Box>
    </Box>
  );
}

function Avatar({
  path,
  left,
  zIndex,
}: {
  path?: string;
  left: string;
  zIndex: number;
}) {
  const isExternal = !!path && /^https?:/.test(path);
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(
    isExternal ? undefined : path
  );
  const { blob } = useMediaBlob(isExternal ? "" : variantPath ?? "");
  const bg = isExternal
    ? `url(${path})`
    : blob
      ? `url(${URL.createObjectURL(blob)})`
      : undefined;

  return (
    <Box
      ref={setMeasureRef}
      position="absolute"
      top="0"
      left={left}
      zIndex={zIndex}
      boxSize="44px"
      borderRadius="full"
      border="2px solid"
      borderColor="bg.canvas"
      bg="bg.muted"
      color="fg.brand"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {bg ? (
        <Box
          position="absolute"
          inset="0"
          backgroundImage={bg}
          backgroundSize="cover"
          backgroundPosition="center"
        />
      ) : (
        <Icon name="user-solid" boxSize="55%" />
      )}
    </Box>
  );
}

// Confetti palette — brand greens plus warm accents, pulled from brand tokens
// via CSS vars so the burst stays on-brand and re-themes with the token set.
const CONFETTI_COLORS = [
  "var(--chakra-colors-gf-green-500)",
  "var(--chakra-colors-gf-green-300)",
  "var(--chakra-colors-gf-green-100)",
  "var(--chakra-colors-gf-orange-primary)",
  "var(--chakra-colors-gf-red-legacy-primary)",
];

const CONFETTI_COUNT = 28;

/**
 * A one-shot confetti burst behind the card. Particles spray from the
 * bottom-right corner up toward the top-left in a fast fan, tumbling and
 * fading as they go — no fall-back. Deterministic per-mount (seeded by index)
 * so re-renders don't reshuffle mid-flight; the burst replays only when the
 * component remounts (i.e. a fresh match).
 */
function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => {
        // Cheap seeded pseudo-random so pieces vary without Math.random in
        // render (keeps output stable across re-renders for the same mount).
        const r = (n: number) => {
          const x = Math.sin(i * 12.9898 + n * 78.233) * 43758.5453;
          return x - Math.floor(x);
        };
        // Shoot from the bottom-right corner toward the top-left in a fanned
        // spray; jitter the even spacing so it reads as a burst, not a comb.
        const base = Math.PI * 1.25; // points up-and-left (−x, −y)
        const spread = Math.PI * 0.5; // ~90° fan around that heading
        const angle = base + (i / CONFETTI_COUNT - 0.5) * spread + (r(0) - 0.5) * 0.3;
        const dist = 130 + r(1) * 200;
        return {
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist,
          rotate: r(2) * 720 - 360,
          delay: r(3) * 0.1,
          size: 6 + r(4) * 6,
          round: r(5) > 0.5,
        };
      }),
    []
  );

  return (
    <Box
      position="absolute"
      inset="0"
      overflow="visible"
      pointerEvents="none"
      aria-hidden="true"
      zIndex={0}
    >
      {pieces.map((p, i) => (
        <MotionDiv
          key={i}
          initial={{ opacity: 1, x: 0, y: 0, scale: 0.4, rotate: 0 }}
          animate={{
            // Expand outward from the origin and fade — no fall-back.
            opacity: [1, 1, 0],
            x: p.x,
            y: p.y,
            scale: 1,
            rotate: p.rotate,
          }}
          transition={{
            duration: 0.7,
            delay: p.delay,
            ease: "easeOut",
            times: [0, 0.6, 1],
          }}
          style={{
            position: "absolute",
            // Origin: bottom-right corner. Particles fan up-and-left from here.
            top: "100%",
            left: "100%",
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.round ? "9999px" : "1px",
          }}
        />
      ))}
    </Box>
  );
}
